import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DealCard from "@/components/DealCard";
import SaleMarquee from "@/components/SaleMarquee";
import AdBanner from "@/components/AdBanner";
import DealOfWeek from "@/components/DealOfWeek";
import Seo from "@/components/Seo";
import { TID } from "@/constants/testIds";
import { MagnifyingGlass, Lightning, Gauge, Storefront, ArrowRight, Rocket } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const { user } = useAuth();
  const isAdmin = user && user.owns_platform;
  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fireworks = useCallback(() => {
    const end = Date.now() + 800;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors: ["#FF007F", "#00FFFF", "#FFD600", "#39FF14"] });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors: ["#FF007F", "#00FFFF", "#FFD600", "#39FF14"] });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    const [c, l] = await Promise.all([
      api.get("/categories"),
      api.get("/links", { params: q ? { search: q } : {} }),
    ]);
    setCategories(c.data);
    setLinks(l.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setTimeout(fireworks, 600);
    return () => clearTimeout(t);
  }, [load, fireworks]);

  const onSearch = (e) => { e.preventDefault(); load(search); };

  const homeJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Affiliated Connections",
    url: typeof window !== "undefined" ? window.location.origin : "",
    potentialAction: {
      "@type": "SearchAction",
      target: `${typeof window !== "undefined" ? window.location.origin : ""}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  }), []);

  const dealsListLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!links.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Hot affiliate deals",
      itemListElement: links.slice(0, 30).map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${origin}/deal/${l.id}`,
        name: l.title,
      })),
    };
  }, [links]);

  const catImages = {
    "tech-gadgets": "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    "health-fitness": "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80",
    "fashion-beauty": "https://images.unsplash.com/photo-1445205170230-053b83016050?w=600&q=80",
    "home-living": "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80",
    "software-saas": "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80",
    "finance-crypto": "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=600&q=80",
  };

  const visibleCategories = useMemo(() => categories.filter((c) => c.link_count > 0), [categories]);

  return (
    <div>
      <Seo
        title="Fantastic Affiliate Deals in One Place"
        description="Affiliated Connections is your one-stop affiliate marketing deals store — browse the best online deals across tech, health, fashion, home, software, outdoors and more. Real reviews, real prices. Get connected today."
        keywords="affiliate deals, affiliate marketing, online deals, deals store, affiliate links, discount deals, shopping deals, best offers, sponsored links, product deals"
        path="/"
        jsonLd={dealsListLd ? [homeJsonLd, dealsListLd] : homeJsonLd}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="inline-flex items-center gap-2 border border-[#FF007F] px-3 py-1 mb-6 animate-float-up">
            <Lightning size={14} weight="fill" color="#FFD600" />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD600]">Storewide Sale · Every Day</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95] max-w-4xl animate-float-up">
            The Neon Bazaar of<br /><span className="text-[#FF007F]">Fantastic</span> Affiliate <span className="text-[#00FFFF]">Deals</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-2xl animate-float-up">
            Every hot affiliate link in one blazing place. Browse by category, read real reviews, and get connected. Members can spotlight their own links too.
          </p>

          {/* Admin quick access */}
          {isAdmin && (
            <div className="mt-8 flex flex-wrap gap-3 animate-float-up" data-testid="home-admin-quick-access">
              <Link to="/dashboard" data-testid="home-dashboard-button"
                className="flex items-center gap-2 px-5 py-3 bg-[#00FFFF] text-black font-black uppercase tracking-widest text-sm border border-[#00FFFF] hover:bg-[#09090B] hover:text-[#00FFFF] transition-colors">
                <Gauge size={20} weight="bold" /> Go to My Dashboard
              </Link>
              <Link to="/dashboard?tab=links" data-testid="home-managelinks-button"
                className="flex items-center gap-2 px-5 py-3 border border-zinc-600 font-bold uppercase tracking-widest text-sm hover:border-[#FF007F] hover:text-[#FF007F] transition-colors">
                <Storefront size={20} weight="bold" /> Manage My Store
              </Link>
            </div>
          )}

          {/* Search */}
          <form onSubmit={onSearch} className="mt-8 flex max-w-xl animate-float-up">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search deals…"
                data-testid="home-search-input"
                className="w-full bg-zinc-900/90 border border-zinc-700 focus:border-[#00FFFF] outline-none pl-10 pr-3 py-3 text-white"
              />
            </div>
            <button type="submit" data-testid="home-search-button"
              className="px-6 bg-[#FF007F] text-white font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
              Go
            </button>
          </form>
        </div>
      </section>

      <SaleMarquee />

      <AdBanner placement="home" />

      <DealOfWeek />

      {/* CATEGORIES */}
      {visibleCategories.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight">Shop by <span className="text-[#FF007F]">Category</span></h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visibleCategories.map((c, i) => (
            <Link
              key={c.id}
              to={`/category/${c.slug}`}
              data-testid={TID.categoryCard}
              className="group relative h-40 overflow-hidden border border-zinc-800 hover:border-[#FF007F] transition-all animate-float-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <img src={c.image_url || catImages[c.slug] || HERO} alt={c.name}
                onError={(e) => { e.currentTarget.src = HERO; }}
                className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-4">
                <h3 className="font-display font-bold text-lg uppercase leading-tight">{c.name}</h3>
                <span className="font-mono text-xs text-[#00FFFF]">{c.link_count} deals</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* DEALS */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Rocket size={28} weight="fill" color="#FFD600" />
          <h2 className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight">Hot <span className="text-[#00FFFF]">Deals</span></h2>
        </div>
        {loading ? (
          <p className="text-zinc-500 font-mono">Loading deals…</p>
        ) : links.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center">
            <p className="text-zinc-400">No deals yet. {isAdmin ? "Head to your dashboard to add the first one!" : "Check back soon — deals are landing daily."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {links.map((l, i) => <DealCard key={l.id} link={l} index={i} />)}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-8 grid md:grid-cols-2 gap-5">
        <div className="border-2 border-[#FF007F] bg-[#18181B] p-8 flex flex-col justify-between gap-4 hard-shadow">
          <div>
            <h3 className="font-display text-2xl font-black uppercase">Got an affiliate link?</h3>
            <p className="text-zinc-400 mt-2">Spotlight it with a glowing SPONSORED badge. Plans from $15.</p>
          </div>
          <Link to="/submit" data-testid="home-cta-submit"
            className="flex items-center gap-2 px-6 py-3 bg-[#FF007F] text-white font-black uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors w-fit">
            Post Your Link <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
        <div className="border-2 border-[#FFD600] bg-[#18181B] p-8 flex flex-col justify-between gap-4 hard-shadow-cyan">
          <div>
            <h3 className="font-display text-2xl font-black uppercase">Want the whole platform?</h3>
            <p className="text-zinc-400 mt-2">Buy your own branded store + dashboard and run it yourself.</p>
          </div>
          <Link to="/buy-platform" data-testid="home-cta-buy-platform"
            className="flex items-center gap-2 px-6 py-3 bg-[#FFD600] text-black font-black uppercase tracking-widest border border-[#FFD600] hover:bg-transparent hover:text-[#FFD600] transition-colors w-fit">
            Buy the Platform <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
